"use client";

import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import StatusBadge from "@/components/dashboard/StatusBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Play, Download, Key } from "lucide-react";
import { useTranslation } from "react-i18next";

const syncHistory = [
  { date: "2025-02-10", type: "Delta", services: "Exchange, OneDrive", status: "success" as const, objects: 142, size: "1.2 GB", duration: "32 min" },
  { date: "2025-02-09", type: "Full", services: "All Services", status: "success" as const, objects: 1247, size: "24.5 GB", duration: "1h 12min" },
  { date: "2025-02-08", type: "Delta", services: "SharePoint", status: "warning" as const, objects: 89, size: "3.1 GB", duration: "48 min" },
  { date: "2025-02-07", type: "Delta", services: "Exchange", status: "error" as const, objects: 0, size: "0 GB", duration: "12 min" },
  { date: "2025-02-06", type: "Delta", services: "OneDrive, Teams", status: "success" as const, objects: 67, size: "0.8 GB", duration: "27 min" },
];

const TenantDetail = () => {
  const params = useParams();
  const id = params?.id as string | undefined;
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <Link to="/dashboard/tenants" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" /> {String(t("tenantDetail.backToTenants"))}
      </Link>

      <div className="rounded-lg border border-border/50 bg-card p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold">Banque Léman SA</h1>
              <StatusBadge status="success" label="Active" />
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
              <span className="text-muted-foreground">{String(t("tenantDetail.m365TenantId"))}</span>
              <span className="font-mono text-xs">leman-365-prod</span>
              <span className="text-muted-foreground">{String(t("tenantDetail.currentPlan"))}</span>
              <span>Enterprise</span>
            </div>
            <div className="w-64">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>3.2 TB {String(t("tenantDetail.used"))}</span>
                <span>5 TB</span>
              </div>
              <Progress value={64} className="h-2" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm">{String(t("tenantDetail.upgrade"))}</Button>
            <Button variant="outline" size="sm">{String(t("tenantDetail.pause"))}</Button>
            <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">{String(t("tenantDetail.delete"))}</Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
        <h3 className="text-sm font-medium mb-3">{String(t("tenantDetail.quickActions"))}</h3>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="gap-1.5"><Play className="h-3.5 w-3.5" /> {String(t("tenantDetail.triggerDelta"))}</Button>
          <Button variant="outline" size="sm" className="gap-1.5"><Download className="h-3.5 w-3.5" /> {String(t("tenantDetail.downloadExport"))}</Button>
          <Button variant="outline" size="sm" className="gap-1.5"><Key className="h-3.5 w-3.5" /> {String(t("tenantDetail.viewS3"))}</Button>
        </div>
      </div>

      <div className="rounded-lg border border-border/50 bg-card shadow-sm">
        <div className="p-5 border-b border-border/50">
          <h3 className="text-sm font-medium">{String(t("tenantDetail.syncHistory"))}</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">{String(t("tenantDetail.date"))}</TableHead>
              <TableHead className="text-xs">{String(t("tenantDetail.type"))}</TableHead>
              <TableHead className="text-xs">{String(t("tenantDetail.services"))}</TableHead>
              <TableHead className="text-xs">{String(t("tenantDetail.status"))}</TableHead>
              <TableHead className="text-xs">{String(t("tenantDetail.objects"))}</TableHead>
              <TableHead className="text-xs">{String(t("tenantDetail.size"))}</TableHead>
              <TableHead className="text-xs">{String(t("tenantDetail.duration"))}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {syncHistory.map((row, i) => (
              <TableRow key={i}>
                <TableCell className="text-sm">{row.date}</TableCell>
                <TableCell className="text-sm font-medium">{row.type}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{row.services}</TableCell>
                <TableCell><StatusBadge status={row.status} /></TableCell>
                <TableCell className="text-sm text-muted-foreground">{row.objects.toLocaleString()}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{row.size}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{row.duration}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default TenantDetail;
